import React from 'react'

interface YouTubeEmbedProps {
    videoId: string
    title?: string
    className?: string
    aspectRatio?: '16/9' | '9/16' | '1/1'
    autoPlay?: boolean
    controls?: boolean
    loop?: boolean
    start?: number
    end?: number
}

export const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
    videoId,
    title = 'YouTube video player',
    className = '',
    aspectRatio = '16/9',
    autoPlay = false,
    controls = true,
    loop = false,
    start,
    end,
}) => {
    const [isShort, setIsShort] = React.useState(false)

    React.useEffect(() => {
        // Détecter si c'est un short YouTube
        const checkIfShort = async () => {
            try {
                const response = await fetch(
                    `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
                )
                const data = await response.json()
                // Si la largeur < hauteur, c'est probablement un short
                const isVerticalVideo = data?.width < data?.height
                setIsShort(isVerticalVideo)
            } catch (error) {
                console.error('Error checking video type:', error)
            }
        }

        checkIfShort()
    }, [videoId])

    const getAspectRatioPadding = () => {
        switch (aspectRatio) {
            case '9/16':
                return '177.78%'
            case '1/1':
                return '100%'
            case '16/9':
            default:
                return '56.25%'
        }
    }

    const params = new URLSearchParams({
        enablejsapi: '1',
        origin: window.location.origin,
        modestbranding: '1',
        rel: '0',
        showinfo: '0',
        playsinline: '1', // Important pour iOS
        autoplay: autoPlay ? '1' : '0',
        controls: controls ? '1' : '0',
        loop: loop ? '1' : '0',
        ...(start && { start: String(start) }),
        ...(end && { end: String(end) }),
        ...(loop && { playlist: videoId }), // Nécessaire pour la lecture en boucle
    })

    const domain = 'https://www.youtube-nocookie.com' // Version sans cookies
    const videoUrl = `${domain}/embed/${videoId}?${params.toString()}`

    return (
        <div
            className={`youtube-embed ${className}`}
            style={{
                width: '100%',
                maxWidth: isShort ? '315px' : '100%',
                margin: '0 auto',
                position: 'relative',
            }}
        >
            <div
                style={{
                    position: 'relative',
                    paddingBottom: getAspectRatioPadding(),
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
            >
                <iframe
                    src={videoUrl}
                    title={title}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: 'inherit',
                    }}
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    loading="lazy"
                    aria-label={title}
                />
            </div>
        </div>
    )
}

/**
 * Extrait l'ID d'une vidéo YouTube à partir d'une URL YouTube
 * Fonctionne avec les formats d'URL suivants :
 * - https://www.youtube.com/watch?v=ID
 * - https://youtu.be/ID
 * - https://www.youtube.com/embed/ID
 * - https://www.youtube.com/shorts/ID
 * - https://youtube.com/shorts/ID
 * - http://youtube.com/...
 * - www.youtube.com/...
 */
export const extractYouTubeId = (url: string): string | null => {
    if (!url) return null

    try {
        // Nettoyer l'URL et ajouter https:// si nécessaire
        let cleanUrl = url.trim()
        if (!cleanUrl.startsWith('http')) {
            cleanUrl = 'https://' + cleanUrl
        }

        // Extraire l'ID avec une expression régulière plus complète
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/
        const match = RegExp(regExp).exec(cleanUrl)

        if (match?.[2] && match[2].length === 11) {
            return match[2]
        }

        // Essayer avec l'URL complète pour les cas spéciaux
        const urlObj = new URL(cleanUrl)

        // Gestion des URLs youtu.be
        if (urlObj.hostname === 'youtu.be') {
            return urlObj.pathname.slice(1).split('?')[0]
        }

        // Gestion des URLs youtube.com avec paramètre v
        if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
            return urlObj.searchParams.get('v') ?? urlObj.pathname.split('/').pop() ?? null
        }

        return null
    } catch (e) {
        console.error("Erreur lors de l'extraction de l'ID YouTube:", e)
        return null
    }
}

/**
 * Convertit une URL YouTube en iframe HTML
 * Utile pour afficher des vidéos dans du contenu riche (comme un éditeur de texte riche)
 */
export const convertYouTubeUrlToIframe = (url: string): string | null => {
    const videoId = extractYouTubeId(url)
    if (!videoId) return null

    const isShort = url.includes('shorts/')
    const domain = 'https://www.youtube-nocookie.com'
    const videoUrl = `${domain}/embed/${videoId}?modestbranding=1&rel=0&showinfo=0${isShort ? '&playsinline=1' : ''}`

    return `
        <div class="youtube-embed" style="width: 100%; max-width: ${isShort ? '315px' : '100%'}; margin: 0 auto;">
            <div style="position: relative; padding-bottom: ${
                isShort ? '177.78%' : '56.25%'
            }; height: 0; overflow: hidden; border-radius: 12px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                <iframe 
                    src="${videoUrl}" 
                    title="YouTube video player" 
                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: inherit;"
                    allowfullscreen 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    loading="lazy"
                ></iframe>
            </div>
        </div>
    `
}

// Types pour les événements du lecteur YouTube
declare global {
    interface Window {
        onYouTubeIframeAPIReady?: () => void
        YT?: any
    }
}

/**
 * Charge l'API YouTube IFrame de manière asynchrone
 * @returns Une promesse qui se résout lorsque l'API est chargée
 */
export const loadYouTubeAPI = (): Promise<void> => {
    return new Promise((resolve) => {
        if (window.YT?.Player) {
            resolve()
            return
        }

        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        const firstScriptTag = document.getElementsByTagName('script')[0]
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)

        window.onYouTubeIframeAPIReady = () => {
            resolve()
        }
    })
}
